<template>
    <b-modal
        :id="modalId"
        title="Confirme seu WhatsApp"
        header-close-label="Fechar"
        centered
        hide-footer
        no-close-on-backdrop
        @shown="focusCurrentField"
        @hidden="onHidden"
    >
        <div class="mfa-phone-prompt">
            <template v-if="step === 'phone'">
                <p>
                    Usamos esse número para enviar o código de verificação do seu acesso
                    e avisos importantes sobre a sua conta.
                </p>
                <p class="mfa-phone-prompt__alert">
                    Informe um número ao qual você tenha acesso — é para ele que as mensagens
                    serão enviadas sempre que necessário.
                </p>

                <label for="mfa-phone-prompt-phone" class="mfa-phone-prompt__label">Número com WhatsApp</label>
                <!-- `key` nos dois campos: sem ela o Vue reaproveita o mesmo <input>
                     ao trocar de etapa, e o v-mask (que não se desfaz) mascarava o
                     código como telefone, travado em 4 dígitos. -->
                <input
                    id="mfa-phone-prompt-phone"
                    key="mfa-phone-prompt-phone"
                    ref="phoneInput"
                    v-model="phone"
                    v-mask="['(##) ####-####', '(##) #####-####']"
                    type="tel"
                    inputmode="numeric"
                    autocomplete="tel-national"
                    class="form-control"
                    placeholder="DDD + número"
                    :disabled="loading"
                    @paste.prevent="onPhonePaste"
                    @keyup.enter="requestCode"
                />

                <p v-if="error" class="mfa-phone-prompt__error" role="alert">{{ error }}</p>

                <button class="btn btn-primary btn-block mt-3" :disabled="loading || !phoneComplete" @click="requestCode">
                    Enviar código
                </button>
                <button class="btn btn-link btn-block mfa-phone-prompt__dismiss" :disabled="loading" @click="dismiss">
                    Não perguntar novamente — quero continuar apenas por e-mail
                </button>
                <p class="mfa-phone-prompt__hint">
                    Você pode cadastrar seu WhatsApp quando quiser, em Minha Conta &gt; Perfil.
                </p>
            </template>

            <template v-else>
                <p>Enviamos um código pelo WhatsApp para <strong>{{ phoneFormatted }}</strong>.</p>
                <p v-if="sharedWithOtherUser" class="mfa-phone-prompt__alert">
                    Atenção: este número já está cadastrado para outro usuário da corretora.
                </p>

                <label for="mfa-phone-prompt-code" class="mfa-phone-prompt__label">Código recebido</label>
                <input
                    id="mfa-phone-prompt-code"
                    key="mfa-phone-prompt-code"
                    ref="codeInput"
                    v-model="code"
                    type="text"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    maxlength="7"
                    class="form-control mfa-phone-prompt__code"
                    :disabled="loading"
                    @keyup.enter="confirm"
                />

                <p v-if="error" class="mfa-phone-prompt__error" role="alert">{{ error }}</p>

                <button class="btn btn-primary btn-block mt-3" :disabled="loading || !codeComplete" @click="confirm">
                    Confirmar
                </button>
                <div class="d-flex justify-content-between">
                    <button class="btn btn-link" :disabled="loading" @click="editPhone">Corrigir número</button>
                    <button class="btn btn-link" :disabled="loading || resendIn > 0" @click="requestCode">
                        {{ resendIn > 0 ? `Reenviar em ${resendIn}s` : 'Reenviar código' }}
                    </button>
                </div>
            </template>
        </div>
    </b-modal>
</template>

<script>
import {
    getPhonePrompt,
    markPhonePromptShown,
    dismissPhonePrompt,
    requestPhoneVerification,
    savePhone
} from '../../services/mfaService'

const MODAL_ID = 'modal-mfa-phone-prompt'

// Folga para o ModalNovidades, que o layout abre no mounted, aparecer antes:
// este modal espera qualquer outro fechar em vez de empilhar.
const START_DELAY_MS = 1000

// Uma consulta por usuário por carregamento da aplicação. O vue-router reusa a
// instância do layout entre as rotas de topo; ele só é recriado ao passar por
// outro layout (login, onboarding) — e quem decide se exibe, inclusive o
// intervalo para quem só fechou, é o servidor.
const checkedUsers = new Set()

function formatPhone (digits) {
    if (digits.length <= 2) return digits.length ? `(${digits}` : ''
    const number = digits.slice(2, 11)
    const prefixLength = number.length > 8 ? 5 : 4
    if (number.length <= prefixLength) return `(${digits.slice(0, 2)}) ${number}`
    return `(${digits.slice(0, 2)}) ${number.slice(0, prefixLength)}-${number.slice(prefixLength)}`
}

export default {
    name: 'mfa-phone-prompt',
    data () {
        return {
            modalId: MODAL_ID,
            step: 'phone',
            phone: '',
            code: '',
            phoneFormatted: '',
            sharedWithOtherUser: false,
            loading: false,
            error: '',
            resendIn: 0,
            resendTimer: null,
            startTimer: null,
            retryTimer: null,
            userKey: null
        }
    },
    computed: {
        phoneDigits () {
            return this.phone.replace(/\D/g, '')
        },
        phoneComplete () {
            return this.phoneDigits.length === 10 || this.phoneDigits.length === 11
        },
        codeDigits () {
            return this.code.replace(/\D/g, '')
        },
        codeComplete () {
            return this.codeDigits.length === 6
        }
    },
    watch: {
        phone () {
            this.error = ''
        },
        code () {
            this.error = ''
        }
    },
    beforeDestroy () {
        clearTimeout(this.startTimer)
        clearTimeout(this.retryTimer)
        this.stopResendCountdown()
        this.$root.$off('bv::modal::hidden', this.retryOpen)
    },
    methods: {
        // Chamado pelo layout, e só quando o tour de onboarding NÃO vai rodar:
        // o overlay do intro.js fica por cima de qualquer modal e os dois
        // disputariam foco e ESC.
        start () {
            const auth = this.$store.state.auth
            if (!auth.isAuth) return

            this.userKey = `${auth.assinaturaId}:${auth.usuarioId}`
            if (checkedUsers.has(this.userKey)) return

            this.startTimer = setTimeout(this.checkAndOpen, START_DELAY_MS)
        },

        async checkAndOpen () {
            if (checkedUsers.has(this.userKey)) return
            checkedUsers.add(this.userKey)
            try {
                const resp = await getPhonePrompt()
                if (!this._isDestroyed && resp.data && resp.data.shouldPrompt) this.openWhenFree()
            } catch (err) {
                // Silencioso de propósito: o modal é um convite, falhar em
                // decidir se ele aparece não pode virar erro na tela.
            }
        },

        async openWhenFree () {
            if (this._isDestroyed) return

            // Outro modal aberto: espera ele fechar em vez de empilhar.
            if (document.querySelector('.modal.show')) {
                this.$root.$once('bv::modal::hidden', this.retryOpen)
                return
            }

            // Marca a exibição ANTES de abrir: se não gravar, não abre. Abrir
            // sem a marca faria o modal voltar a cada carregamento — inclusive
            // para quem já pediu "não perguntar novamente".
            try {
                await markPhonePromptShown()
            } catch (err) {
                return
            }
            if (!this._isDestroyed) this.$bvModal.show(MODAL_ID)
        },

        retryOpen () {
            clearTimeout(this.retryTimer)
            this.retryTimer = setTimeout(this.openWhenFree, 400)
        },

        // A máscara corta nos 11 primeiros dígitos: "+55 91 98888-7777" colado
        // viraria (55) 91988-8877 — um número válido de OUTRA pessoa, para quem
        // o código seria enviado. Tira o DDI antes de aplicar a máscara.
        onPhonePaste (event) {
            const clipboard = event.clipboardData || window.clipboardData
            let digits = ((clipboard && clipboard.getData('text')) || '').replace(/\D/g, '')
            if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
                digits = digits.slice(2)
            }
            this.phone = formatPhone(digits.slice(0, 11))
        },

        async requestCode () {
            if (!this.phoneComplete || this.loading) return
            this.loading = true
            this.error = ''
            try {
                const resp = await requestPhoneVerification(this.phoneDigits)
                this.phoneFormatted = resp.data.phoneFormatted
                this.sharedWithOtherUser = resp.data.sharedWithOtherUser
                this.code = ''
                this.step = 'code'
                this.startResendCountdown(resp.data.resendCooldownSeconds)
                this.$nextTick(this.focusCurrentField)
            } catch (err) {
                this.error = this.mensagemDoBackend(err) || 'Não foi possível enviar o código. Tente novamente.'
            } finally {
                this.loading = false
            }
        },

        async confirm () {
            if (!this.codeComplete || this.loading) return
            this.loading = true
            this.error = ''
            try {
                const resp = await savePhone(this.phoneDigits, this.codeDigits)
                this.$bvModal.hide(MODAL_ID)
                // Sucesso parcial: o telefone foi salvo, mas o canal não virou
                // WhatsApp. O código já foi consumido — fechar e orientar é
                // melhor do que deixar a pessoa tentando de novo.
                if (resp.data && resp.data.channel !== 'whatsapp') {
                    window.Vue.$noty.warning('Telefone salvo. Para receber os códigos por WhatsApp, ative em Minha Conta > Perfil.')
                } else {
                    window.Vue.$noty.success('WhatsApp confirmado. Os próximos códigos de acesso chegarão por ele.')
                }
            } catch (err) {
                this.error = this.mensagemDoBackend(err) || 'Não foi possível confirmar o código. Tente novamente.'
            } finally {
                this.loading = false
            }
        },

        async dismiss () {
            if (this.loading) return
            this.loading = true
            this.error = ''
            try {
                await dismissPhonePrompt()
                this.$bvModal.hide(MODAL_ID)
            } catch (err) {
                this.error = this.mensagemDoBackend(err) || 'Não foi possível registrar sua preferência. Tente novamente.'
            } finally {
                this.loading = false
            }
        },

        editPhone () {
            this.step = 'phone'
            this.code = ''
            this.error = ''
            this.stopResendCountdown()
            this.$nextTick(this.focusCurrentField)
        },

        focusCurrentField () {
            const field = this.step === 'phone' ? this.$refs.phoneInput : this.$refs.codeInput
            if (field) field.focus()
        },

        startResendCountdown (seconds) {
            this.stopResendCountdown()
            this.resendIn = Math.max(0, seconds || 0)
            if (this.resendIn === 0) return
            this.resendTimer = setInterval(() => {
                this.resendIn -= 1
                if (this.resendIn <= 0) this.stopResendCountdown()
            }, 1000)
        },

        stopResendCountdown () {
            clearInterval(this.resendTimer)
            this.resendTimer = null
            this.resendIn = 0
        },

        onHidden () {
            this.stopResendCountdown()
        },

        // Mesma leitura do mfaVerify: o interceptor global rejeita com o
        // RESPONSE do axios, então o corpo vem em err.data, e o
        // NotificationResponse serializa as mensagens em `message` (singular).
        mensagemDoBackend (err) {
            const corpo = (err && err.data) || err || {}
            const msg = corpo.message
            if (Array.isArray(msg)) return msg[0]
            return typeof msg === 'string' ? msg : null
        }
    }
}
</script>

<style scoped>
.mfa-phone-prompt__label {
    font-weight: 500;
    margin-top: 0.5rem;
}

.mfa-phone-prompt__alert {
    background: #fff8e6;
    border-left: 3px solid #f0ad4e;
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
}

.mfa-phone-prompt__error {
    color: #ea4335;
    font-size: 0.9rem;
    margin: 0.5rem 0 0;
}

.mfa-phone-prompt__code {
    font-size: 1.4rem;
    letter-spacing: 0.4rem;
    text-align: center;
}

.mfa-phone-prompt__dismiss {
    white-space: normal;
}

.mfa-phone-prompt__hint {
    color: #5f6368;
    font-size: 0.85rem;
    text-align: center;
    margin: 0.25rem 0 0;
}
</style>
